import React, { useEffect, useState } from "react";
import { StatTilesSkeleton, TableSkeleton } from "../UI/Skeleton";
import { Calendar, CheckCircle2, XCircle, Loader2, Search, ChevronLeft, ChevronRight, Clock, User, Building, Phone } from "lucide-react";
import api from "../../utils/axios";
import toast from "react-hot-toast";

// One entry per booking status: label, who acted, pill colours and icon
const STATUS_META = {
  pending:             { label: "Pending",   byWho: "",          icon: Clock,        pill: "bg-amber-50 text-amber-800 border-amber-200",  dot: "bg-amber-500" },
  confirmed:           { label: "Confirmed", byWho: "by Vendor", icon: CheckCircle2, pill: "bg-green-50 text-green-800 border-green-200",  dot: "bg-green-500" },
  rejected:            { label: "Rejected",  byWho: "by Vendor", icon: XCircle,      pill: "bg-red-50 text-red-800 border-red-200",        dot: "bg-red-500" },
  cancelled_by_user:   { label: "Cancelled", byWho: "by User",   icon: User,         pill: "bg-gray-50 text-gray-700 border-gray-200",     dot: "bg-gray-400" },
  cancelled_by_vendor: { label: "Cancelled", byWho: "by Vendor", icon: Building,     pill: "bg-orange-50 text-orange-800 border-orange-200", dot: "bg-orange-500" },
  completed:           { label: "Completed", byWho: "",          icon: CheckCircle2, pill: "bg-emerald-50 text-emerald-800 border-emerald-200", dot: "bg-emerald-500" },
};

const TH = "px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap";

const VendorBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  const loadBookings = async () => {
    try {
      setLoading(true);
      const params = {
        status: statusFilter || undefined,
        page,
        limit,
        search: searchQuery || undefined
      };
      
      const res = await api.get("/api/vendor/bookings", { params });
      
      if (res.data.success) {
        setBookings(res.data.data || []);
        setPages(res.data.pagination?.pages || 1);
        setTotal(res.data.pagination?.total || 0);
      }
    } catch (err) {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const res = await api.get("/api/vendor/bookings/stats");
      if (res.data.success) {
        setStats(res.data.data);
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    loadBookings();
  }, [statusFilter, page, limit, searchQuery]);

  useEffect(() => {
    loadStats();
  }, []);

  const handleConfirm = async (id) => {
    if (!window.confirm("Confirm this booking?")) return;
    try {
      setActionLoadingId(id);
      await api.put(`/api/vendor/bookings/${id}/confirm`, {
        response_message: "Booking confirmed by vendor",
      });
      toast.success("Booking confirmed");
      loadBookings();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to confirm");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt("Enter rejection reason:");
    if (!reason) return;
    try {
      setActionLoadingId(id);
      await api.put(`/api/vendor/bookings/${id}/reject`, {
        rejection_reason: reason,
      });
      toast.success("Booking rejected");
      loadBookings();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject");
    } finally {
      setActionLoadingId(null);
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount) => `₹${new Intl.NumberFormat('en-IN').format(amount)}`;

  return (
    <div className="space-y-5 lg:space-y-6">
      {/* Header & Filters */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] p-5 sm:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <div>
            <h1 className="font-['Manrope'] text-xl font-bold tracking-tight text-gray-900 mb-1 flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-purple-600 to-pink-600 shadow-md shadow-purple-600/20">
                <Calendar className="w-[18px] h-[18px] text-white" />
              </span>
              Bookings ({total})
            </h1>
            <p className="text-sm text-gray-500">Manage customer bookings with complete timeline</p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1 sm:w-72 sm:flex-none">
              <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                placeholder="Search reference, customer, venue..."
                className="h-11 w-full pl-10 pr-4 text-sm border border-gray-200 rounded-xl bg-white hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <select
              className="h-11 px-3.5 text-sm border border-gray-200 rounded-xl bg-white hover:border-gray-300 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-500 transition-all"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled_by_user">Cancelled by User</option>
              <option value="cancelled_by_vendor">Cancelled by Vendor</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {/* Stats Row */}
        {!stats && loading && <StatTilesSkeleton />}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Total</p>
              <p className="mt-1.5 font-['Manrope'] text-2xl font-extrabold tracking-tight text-gray-900 tabular-nums">{stats.total || 0}</p>
            </div>
            <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Pending</p>
              <p className="mt-1.5 font-['Manrope'] text-2xl font-extrabold tracking-tight text-amber-600 tabular-nums">{stats.pending || 0}</p>
            </div>
            <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Confirmed</p>
              <p className="mt-1.5 font-['Manrope'] text-2xl font-extrabold tracking-tight text-green-600 tabular-nums">{stats.confirmed || 0}</p>
            </div>
            <div className="rounded-xl border border-gray-200/70 bg-gray-50/60 p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">Revenue</p>
              <p className="mt-1.5 font-['Manrope'] text-2xl font-extrabold tracking-tight bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent tabular-nums">{formatAmount(stats.totalRevenue || 0)}</p>
            </div>
          </div>
        )}
      </div>

      {/* Bookings Table */}
      <div className="bg-white rounded-2xl border border-gray-200/70 shadow-[0_1px_3px_rgba(16,24,40,0.04)] overflow-hidden">
        {/* Table header bar */}
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2.5">
            <h2 className="font-['Manrope'] text-base font-bold text-gray-900">All bookings</h2>
            <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-0.5 text-xs font-semibold text-gray-600 tabular-nums">{total}</span>
          </div>
          {pages > 1 && (
            <p className="text-xs font-medium text-gray-500 tabular-nums">Page {page} of {pages}</p>
          )}
        </div>

        {loading ? (
          <TableSkeleton rows={6} cols={6} />
        ) : bookings.length === 0 ? (
          <div className="py-16 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-purple-50">
              <Calendar className="w-7 h-7 text-purple-400" />
            </div>
            <h3 className="font-['Manrope'] text-lg font-bold text-gray-900 mb-1">No bookings found</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              Adjust your search or filter above to see bookings
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full min-w-[980px] text-sm">
                <thead className="bg-gray-50/80 border-b border-gray-100">
                  <tr>
                    <th className={`${TH} pl-5`}>Customer</th>
                    <th className={TH}>Event</th>
                    <th className={TH}>Venue</th>
                    <th className={`${TH} text-right`}>Amount</th>
                    <th className={TH}>Status</th>
                    <th className={TH}>Timeline</th>
                    <th className={`${TH} pr-5 text-right lg:sticky right-0 bg-gray-50`}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {bookings.map((booking) => (
                    <BookingRow
                      key={booking._id}
                      booking={booking}
                      actionLoadingId={actionLoadingId}
                      onConfirm={handleConfirm}
                      onReject={handleReject}
                      formatDateTime={formatDateTime}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-gray-100">
              {bookings.map((booking) => (
                <BookingCard
                  key={booking._id}
                  booking={booking}
                  actionLoadingId={actionLoadingId}
                  onConfirm={handleConfirm}
                  onReject={handleReject}
                />
              ))}
            </div>

            {/* Footer: range + rows per page + pager */}
            <div className="px-5 py-3.5 bg-gray-50/60 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-gray-500">
                Showing <span className="font-semibold text-gray-900 tabular-nums">{(page - 1) * limit + 1}–{Math.min(page * limit, total)}</span> of{" "}
                <span className="font-semibold text-gray-900 tabular-nums">{total}</span>
              </p>

              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-gray-500 whitespace-nowrap">
                  Rows
                  <select
                    className="h-9 rounded-lg border border-gray-200 bg-white px-2.5 text-sm font-medium text-gray-900 focus:outline-none focus:ring-4 focus:ring-purple-500/10 focus:border-purple-400"
                    value={limit}
                    onChange={(e) => {
                      setLimit(Number(e.target.value));
                      setPage(1);
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </label>

                <div className="flex items-center gap-1.5">
                  <button
                    disabled={page === 1}
                    onClick={() => setPage(p => p - 1)}
                    aria-label="Previous page"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className="min-w-[4.5rem] text-center text-sm font-semibold text-gray-900 tabular-nums">
                    {page} / {pages}
                  </span>
                  <button
                    disabled={page === pages}
                    onClick={() => setPage(p => p + 1)}
                    aria-label="Next page"
                    className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:border-purple-200 hover:bg-purple-50 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Shared cell pieces ───────────────────────────────────────────────────────
const formatAmount = (amount) => `₹${new Intl.NumberFormat('en-IN').format(amount || 0)}`;

const customerName = (booking) => booking.guest_details?.name || booking.user_id?.full_name || "Guest";

const initialsOf = (name) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0].toUpperCase()).join("");

const StatusPill = ({ status }) => {
  const meta = STATUS_META[status] || { label: status.replace(/_/g, " "), byWho: "", pill: "bg-gray-100 text-gray-700 border-gray-200", dot: "bg-gray-400" };
  return (
    <div>
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${meta.pill}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
        {meta.label}
      </span>
      {meta.byWho && <div className="mt-1 text-[11px] text-gray-500">{meta.byWho}</div>}
    </div>
  );
};

// Calendar-style date tile
const DateTile = ({ date }) => {
  const d = new Date(date);
  return (
    <div className="flex h-11 w-11 flex-shrink-0 flex-col items-center justify-center rounded-xl border border-purple-100 bg-purple-50/70 leading-none">
      <span className="text-[9px] font-bold uppercase tracking-wider text-gray-500">{d.toLocaleDateString('en-IN', { month: 'short' })}</span>
      <span className="mt-0.5 font-['Manrope'] text-base font-extrabold text-gray-900 tabular-nums">{d.getDate()}</span>
    </div>
  );
};

const RowActions = ({ booking, actionLoadingId, onConfirm, onReject }) => {
  const busy = actionLoadingId === booking._id;
  return (
    <div className="flex gap-2 justify-end">
      <button
        disabled={busy}
        onClick={() => onReject(booking._id)}
        className="inline-flex items-center justify-center gap-1.5 h-9 px-3 bg-white border border-gray-200 text-gray-700 font-semibold rounded-lg text-xs hover:bg-red-50 hover:border-red-200 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5" />}
        Reject
      </button>
      <button
        disabled={busy}
        onClick={() => onConfirm(booking._id)}
        className="inline-flex items-center justify-center gap-1.5 h-9 px-3.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold rounded-lg text-xs shadow-md shadow-purple-600/20 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
        Confirm
      </button>
    </div>
  );
};

// ─── Desktop row ──────────────────────────────────────────────────────────────
const BookingRow = ({ booking, actionLoadingId, onConfirm, onReject, formatDateTime }) => {
  const status = booking.booking_status;
  const isPending = status === 'pending';
  const isConfirmed = status === 'confirmed';
  const isClosed = ['rejected', 'cancelled_by_user', 'cancelled_by_vendor'].includes(status);
  const closedDate = booking.vendor_response?.cancelled_at || booking.updatedAt;
  const confirmedDate = booking.vendor_response?.responded_at;
  const name = customerName(booking);

  // Timeline steps: requested, then confirmed / rejected / cancelled
  const steps = [{ label: 'Requested', at: formatDateTime(booking.createdAt), dot: 'bg-gray-300' }];
  if (isConfirmed && confirmedDate) steps.push({ label: 'Confirmed', at: formatDateTime(confirmedDate), dot: 'bg-green-500' });
  if (isClosed && closedDate) steps.push({ label: status === 'rejected' ? 'Rejected' : 'Cancelled', at: formatDateTime(closedDate), dot: 'bg-red-500' });

  return (
    <tr className="group hover:bg-purple-50/30 transition-colors">
      {/* Customer + reference */}
      <td className="pl-5 pr-4 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-pink-100 text-xs font-bold text-gray-700">
            {initialsOf(name) || <User className="w-4 h-4" />}
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900">{name}</div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
              <span className="inline-flex items-center gap-1">
                <Phone className="w-3 h-3 text-gray-400" />
                {booking.contact_phone || booking.user_id?.phone}
              </span>
              <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-600">#{booking.booking_reference}</span>
            </div>
          </div>
        </div>
      </td>

      {/* Event */}
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <DateTile date={booking.event_date} />
          <div>
            <div className="font-semibold text-gray-900">
              {new Date(booking.event_date).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
            </div>
            <div className="mt-0.5 text-xs text-gray-500 capitalize">
              {booking.slot} • {booking.number_of_guests} guests
            </div>
          </div>
        </div>
      </td>

      {/* Venue */}
      <td className="px-4 py-4 whitespace-nowrap">
        <div className="font-medium text-gray-900">{booking.venue_id?.name}</div>
        <div className="mt-0.5 text-xs text-gray-500 capitalize">
          {booking.function_type?.replace(/_/g, ' ')}
        </div>
      </td>

      {/* Amount */}
      <td className="px-4 py-4 text-right whitespace-nowrap">
        <div className="font-['Manrope'] font-bold text-gray-900 tabular-nums">
          {formatAmount(booking.pricing_details?.total_amount)}
        </div>
        {booking.payment_status && (
          <span className="mt-1 inline-block rounded-md bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-600 capitalize">{booking.payment_status}</span>
        )}
      </td>

      {/* Status */}
      <td className="px-4 py-4 whitespace-nowrap">
        <StatusPill status={status} />
      </td>

      {/* Timeline */}
      <td className="px-4 py-4 text-xs text-gray-600">
        <ol className="relative space-y-2">
          {steps.map((step, i) => (
            <li key={step.label} className="relative flex items-start gap-2.5 whitespace-nowrap">
              {i < steps.length - 1 && <span className="absolute left-[3px] top-3 h-full w-px bg-gray-200" />}
              <span className={`relative mt-1 h-[7px] w-[7px] flex-shrink-0 rounded-full ${step.dot}`} />
              <span>
                <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-400">{step.label}</span>
                {step.at}
              </span>
            </li>
          ))}
        </ol>
        {isClosed && booking.vendor_response?.rejection_reason && (
          <div className="mt-2 max-w-[16rem] rounded-lg border border-red-100 bg-red-50/60 px-2 py-1 text-red-700">
            "{booking.vendor_response.rejection_reason}"
          </div>
        )}
      </td>

      {/* Actions */}
      <td className="lg:sticky right-0 bg-white group-hover:bg-[#faf7ff] pl-4 pr-5 py-4 text-right whitespace-nowrap shadow-[-8px_0_12px_-8px_rgba(16,24,40,0.08)] transition-colors">
        {isPending ? (
          <RowActions booking={booking} actionLoadingId={actionLoadingId} onConfirm={onConfirm} onReject={onReject} />
        ) : (
          <span className="text-xs text-gray-400">No action needed</span>
        )}
      </td>
    </tr>
  );
};

// ─── Mobile card ──────────────────────────────────────────────────────────────
const BookingCard = ({ booking, actionLoadingId, onConfirm, onReject }) => {
  const name = customerName(booking);
  return (
    <div className="p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <DateTile date={booking.event_date} />
          <div className="min-w-0">
            <div className="font-semibold text-gray-900 truncate">{name}</div>
            <div className="mt-0.5 text-xs text-gray-500 capitalize truncate">
              {booking.slot} • {booking.number_of_guests} guests
            </div>
          </div>
        </div>
        <StatusPill status={booking.booking_status} />
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
        <span className="truncate">{booking.venue_id?.name}</span>
        <span className="font-['Manrope'] text-sm font-bold text-gray-900 tabular-nums">{formatAmount(booking.pricing_details?.total_amount)}</span>
      </div>
      <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <Phone className="w-3 h-3 text-gray-400" />
          {booking.contact_phone || booking.user_id?.phone}
        </span>
        <span className="rounded-md bg-gray-100 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-gray-600">#{booking.booking_reference}</span>
      </div>
      {booking.booking_status === 'pending' && (
        <RowActions booking={booking} actionLoadingId={actionLoadingId} onConfirm={onConfirm} onReject={onReject} />
      )}
    </div>
  );
};

export default VendorBookings;
